import { Box, Button } from "@amzn/awsui-components-console";

import { ChatMessageFromHistory } from "@/types/chat-history";
import { ChatResponse, ProcessingStepSection } from "@/types/chat-response";
import { UserFromApi, UserObject } from "@/types/user";

import {
  AssetIdIconikItem,
  ChatMessage,
  InitialIconikItem,
  LinkNodeElement,
  MessageProps,
  MetadataFilters,
  NodeElement,
  NodeText,
  TablePropsFromMarkdown,
} from "./types";

export const isProcessingStep = (response: ChatResponse) =>
  Object.values(ProcessingStepSection).includes(
    response.section as ProcessingStepSection
  ) ||
  // Dynamic section names like "Tool: Getting sample SQL patterns"
  // and "Execution Summary" are also processing steps.
  (typeof response.section === "string" &&
    ((response.section as string).startsWith("Tool:") ||
      (response.section as string) === "Execution Summary"));

export const getHumanMessage = (
  user: UserObject | UserFromApi,
  { content, timestamp, hasMore = false }: MessageProps
): ChatMessage => {
  let name: string | undefined;
  let firstName: string | undefined;
  let lastName: string | undefined;

  if ("attributes" in user) {
    name = user.attributes.name;
    firstName = user.attributes.given_name;
    lastName = user.attributes.family_name;
  } else {
    name = user.name;
    firstName = user.givenName;
    lastName = user.familyName;
  }

  const initials: string = (firstName?.[0] ?? "") + (lastName?.[0] ?? "");

  return {
    author: {
      type: "user",
      name,
      initials,
    },
    content,
    timestamp: timestamp ?? new Date().toLocaleTimeString(),
    hasMore,
  };
};

export const getGenAiMessage = ({
  content,
  timestamp,
  hasMore = false,
}: MessageProps): ChatMessage => {
  return {
    author: {
      type: "gen-ai",
      loading: hasMore,
    },
    content,
    timestamp: timestamp ?? new Date().toLocaleTimeString(),
    hasMore,
  };
};

export const getLoadingMessage = (toggleSteps: () => void): ChatMessage => {
  return {
    author: {
      type: "gen-ai",
      loading: true,
    },
    content: (
      <Box color="text-status-inactive">
        Generating a response. Open the{" "}
        <Button variant="inline-link" onClick={toggleSteps}>
          Processing Steps
        </Button>{" "}
        to learn more.
      </Box>
    ),
    timestamp: new Date().toLocaleTimeString(),
    hasMore: false,
  };
};

/**
 * Read the display text out of a chat frame's `response` field.
 *
 * The field normally holds a JSON-encoded response body whose own `response`
 * key is the text to render. Some senders put bare text there instead, and
 * this runs inside a `setState` updater called from an effect — an exception
 * escapes into React's render phase and unmounts the whole tree, so a single
 * malformed frame would black-screen the app rather than showing one bad
 * message. Treat anything that isn't the expected envelope as plain text.
 */
const extractResponseText = (response: string): string => {
  if (typeof response !== "string") return "";

  try {
    const parsed = JSON.parse(response);
    if (parsed && typeof parsed === "object" && "response" in parsed) {
      const inner = (parsed as { response: unknown }).response;
      return typeof inner === "string" ? inner : String(inner ?? "");
    }
    // Valid JSON but not a response envelope (a bare string, number, or some
    // other object): show what came through instead of dropping it.
    return typeof parsed === "string" ? parsed : response;
  } catch {
    return response;
  }
};

export const appendToChatMessages =
  ({ final, stream, response }: ChatResponse) =>
  (prevMessages: ChatMessage[]): ChatMessage[] => {
    const lastMessage = prevMessages[prevMessages.length - 1];

    const content = extractResponseText(response);

    // If has more, edit the incoming message.
    const willHaveMore = !final && stream;

    return [
      ...prevMessages.slice(0, -1),
      getGenAiMessage({
        content: lastMessage.hasMore ? lastMessage.content + content : content,
        hasMore: willHaveMore,
      }),
    ];
  };

const convertToKey = (col: string): string =>
  col.toLocaleLowerCase().replace(/\s/g, "-");

const findElementTag = (tag: string) => (child: NodeElement | NodeText) =>
  child.type === "element" && child.tagName === tag;

/**
 * Recursively extracts text content from a tree of element/text nodes.
 * Handles nested formatting elements like <strong>, <em>, <code>, etc.
 */
const extractTextContent = (nodes: (NodeElement | NodeText)[]): string =>
  nodes
    .map((node) => {
      if (node.type === "text") return (node as NodeText).value ?? "";
      if (node.type === "element")
        return extractTextContent((node as NodeElement).children ?? []);
      return "";
    })
    .join("");

const parseValueFromTableCell = ({ children }: NodeElement): string => {
  const link = children.find(findElementTag("a"));
  if (link) {
    return (link as LinkNodeElement).properties.href;
  }

  // Recursively extract text from any nested elements (strong, em, code, etc.)
  return extractTextContent(children);
};

// Checks if the items have game-id, action-id, and assets.
const checkIconikItems = (items: Record<string, string>[]): boolean =>
  items.length > 0 && !!items[0]["game-id"] && !!items[0]["action-id"];

// Checks if the items have asset-id (multi-sport direct asset lookup).
const checkAssetIdItems = (items: Record<string, string>[]): boolean =>
  items.length > 0 && !!items[0]["asset-id"];

const mapToIconikItem = (item: Record<string, string>): InitialIconikItem => ({
  gameId: item["game-id"],
  playId: item["action-id"],
  // Optional Description column carries the play-by-play description so
  // no-media plays can show it (absent when the agent omits the column).
  ...(item["description"] ? { description: item["description"] } : {}),
});

/** Matches the agent's ``[metadata_filters: {...}]`` block in a response. */
const METADATA_FILTERS_BLOCK = /\[metadata_filters:\s*(\{[^{}]*\})\s*\]/;

/**
 * Extract response-level Iconik metadata filters from an agent answer.
 *
 * The agent emits filters as a ``[metadata_filters: {"FeedType": "Films"}]``
 * block in its markdown response (alongside the Game ID | Action ID table).
 * This pulls that JSON object out of the raw message text so it can be
 * forwarded to the ``/assets`` call and narrow the delivered clips.
 *
 * Fails safe: any absence, malformed JSON, or non-object payload returns
 * ``undefined`` (the answer is treated as unfiltered — identical to today's
 * behaviour). Only string-valued entries are kept, matching the field/value
 * contract the ``/assets`` API enforces; if nothing valid remains the result
 * is ``undefined`` so no ``filters`` query parameter is sent.
 */
export const parseMetadataFilters = (
  content: string
): MetadataFilters | undefined => {
  const match = content.match(METADATA_FILTERS_BLOCK);
  if (!match) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(match[1]);
  } catch {
    return undefined;
  }
  if (
    typeof parsed !== "object" ||
    parsed === null ||
    Array.isArray(parsed)
  ) {
    return undefined;
  }

  const filters: MetadataFilters = {};
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof value === "string") {
      filters[key] = value;
    }
  }
  return Object.keys(filters).length > 0 ? filters : undefined;
};

/**
 * Remove the ``[metadata_filters: {...}]`` block from an answer's display text.
 *
 * The raw block is machine-oriented (it drives the ``/assets`` fetch) and
 * would otherwise render as literal ``[metadata_filters: {...}]`` in the chat
 * bubble. This strips it and tidies the surrounding whitespace so the prose
 * reads cleanly; the parsed filters are surfaced separately as a friendly
 * summary. Content without a block is returned unchanged.
 */
export const stripMetadataFiltersBlock = (content: string): string =>
  content
    .replace(METADATA_FILTERS_BLOCK, "")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/** Friendly labels for the documented Iconik filter fields. */
const FILTER_LABEL_OVERRIDES: Record<string, string> = {
  FeedType: "Feed Type",
  "NBA-ActionType": "Action Type",
  "NBA-ActionSubType": "Action Sub-Type",
  "NBA-Descriptor": "Descriptor",
  nbaCourtRegion: "Court Region",
  NBAShotResult: "Shot Result",
  NBAShotDistances: "Shot Distance",
  seasontype_nba: "Season Type",
  evsXTCameraLabel: "Camera",
  "Venue-NBA": "Venue",
};

/**
 * Turn an Iconik metadata field name into a human-readable label.
 *
 * Documented fields get a curated label; anything else is prettified
 * generically: a leading ``NBA``/``nba`` prefix is dropped and
 * snake_case / kebab-case / camelCase boundaries become spaced, Title-cased
 * words. Falls back to the raw key if prettifying leaves nothing.
 */
export const formatMetadataFilterLabel = (key: string): string => {
  const override = FILTER_LABEL_OVERRIDES[key];
  if (override) return override;
  const pretty = key
    .replace(/^nba[-_]?/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return pretty || key;
};

// ---------------------------------------------------------------------------
// Search context block (multi-sport route)
// ---------------------------------------------------------------------------

/** Matches the agent's ``[search_context: {...}]`` block in a response. */
const SEARCH_CONTEXT_BLOCK = /\[search_context:\s*(\{[^{}]*\})\s*\]/;

/**
 * Search context metadata parsed from a multi-sport agent response.
 * Shows what query was used to find clips in Iconik.
 */
export interface SearchContext {
  sport: string;
  query: string;
  results_found: number;
  relevant_fields: string[];
}

/**
 * Extract the search context from an agent answer's ``[search_context: {...}]``
 * block. Returns undefined when the block is absent or malformed.
 */
export const parseSearchContext = (
  content: string
): SearchContext | undefined => {
  const match = content.match(SEARCH_CONTEXT_BLOCK);
  if (!match) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(match[1]);
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return undefined;
  }

  const obj = parsed as Record<string, unknown>;
  const sport = typeof obj.sport === "string" ? obj.sport : "";
  const query = typeof obj.query === "string" ? obj.query : "";
  const results_found =
    typeof obj.results_found === "number" ? obj.results_found : 0;
  const relevant_fields = Array.isArray(obj.relevant_fields)
    ? (obj.relevant_fields as unknown[]).filter(
        (v): v is string => typeof v === "string"
      )
    : [];

  if (!sport && !query) return undefined;

  return { sport, query, results_found, relevant_fields };
};

/**
 * Remove the ``[search_context: {...}]`` block from display text.
 */
export const stripSearchContextBlock = (content: string): string =>
  content
    .replace(SEARCH_CONTEXT_BLOCK, "")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const mapToAssetIdItem = (item: Record<string, string>): AssetIdIconikItem => ({
  assetId: item["asset-id"],
});

export const parseColumnsAndItems = (
  node: NodeElement
): TablePropsFromMarkdown => {
  const children = Array.from(node.children || []);
  const thead = children.find(findElementTag("thead")) as
    | NodeElement
    | undefined;
  const tbody = children.find(findElementTag("tbody")) as
    | NodeElement
    | undefined;

  // Guard against malformed table markdown missing thead or tbody
  if (!thead || !tbody) {
    return { columnDefinitions: [], items: [], areIconikLinks: false };
  }

  const columns: string[] = [];

  // Fetch the header row <tr>.
  const headerRow = Array.from(thead.children).find(
    findElementTag("tr")
  ) as NodeElement | undefined;

  if (!headerRow) {
    return { columnDefinitions: [], items: [], areIconikLinks: false };
  }

  // Fetch the header cells <th>.
  headerRow.children.forEach((cell: NodeElement | NodeText) => {
    if (cell.type === "element" && cell.tagName === "th") {
      const value = extractTextContent((cell as NodeElement).children);
      columns.push(value);
    }
  });

  // Have keys to use for objects.
  const keys = columns.map(convertToKey);

  // Fetch the items.
  const items: Record<string, string>[] = [];

  // Fetch each row <tr> and corresponding cell <td>.
  tbody.children.forEach((row: NodeElement | NodeText) => {
    if (row.type === "element" && row.tagName === "tr") {
      let index = 0;
      const item: Record<string, string> = {};

      Array.from(row.children).forEach((cell: NodeElement | NodeText) => {
        if (cell.type === "element" && cell.tagName === "td") {
          const columnName = keys[index];
          item[columnName] = parseValueFromTableCell(cell);
          index++;
        }
      });

      items.push(item);
    }
  });

  // Check if the items are Iconik Links
  if (checkIconikItems(items)) {
    return {
      // Ignore column definitions
      columnDefinitions: [],
      items: items.map(mapToIconikItem),
      areIconikLinks: true,
    };
  }

  // Check if the items are direct Asset ID links (multi-sport)
  if (checkAssetIdItems(items)) {
    return {
      columnDefinitions: [],
      items: items.map(mapToAssetIdItem),
      areIconikLinks: true,
    };
  }

  // Generate column definitions from columns.
  const columnDefinitions: TablePropsFromMarkdown["columnDefinitions"] =
    columns.map((header, index) => ({
      id: keys[index],
      header,
      cell: (item: Record<string, string>) => item[keys[index]],
      sortingField: keys[index],
    }));

  return {
    columnDefinitions,
    items,
    areIconikLinks: false,
  };
};

export const mapToRenderMessage =
  (user: UserObject | UserFromApi) =>
  ({
    author,
    message: content,
    timestamp,
  }: ChatMessageFromHistory): ChatMessage =>
    author === "Bot"
      ? getGenAiMessage({ content, timestamp })
      : getHumanMessage(user, { content, timestamp });
